import React from "react";
import { useNavigate } from "react-router-dom";
import { makeAPI } from "../services/api";

const PAGE_SIZE = 50;

const SORT_OPTIONS = [
    { value: "newest", label: "Newest" },
    { value: "updated", label: "Recently Updated" },
    { value: "popular", label: "Trending" },
    { value: "az", label: "A to Z" }
];

function buildGameImage(game) {
    if (!game?.assets?.length) return { url: "", hasPreferred: false };

    const rectangularSizes = ["1280x720", "1280x550", "512x384", "512x512"];
    const preferredAsset = rectangularSizes
        .map((size) => game.assets.find((asset) => typeof asset === "string" && asset.includes(size)))
        .find(Boolean);
    const fallbackAsset =
        game.assets.find((asset) => typeof asset === "string" && asset.includes("512x512")) ||
        game.assets.find((asset) => typeof asset === "string") ||
        "";

    const selectedAsset = preferredAsset || fallbackAsset;

    return {
        url: selectedAsset ? `https://img.gamedistribution.com/${selectedAsset}` : "",
        hasPreferred: Boolean(preferredAsset)
    };
}

function slugifyGame(game) {
    const base = (game?.slug || game?.title_slug || game?.title || "game")
        .toString()
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

    return base || "game";
}

function createInitialFilter() {
    return {
        search: "",
        category_slugs: [],
        tag_slugs: [],
        mobile_mode: "",
        sortby: "newest"
    };
}

export default function Games() {
    const navigate = useNavigate();
    const [filters, setFilters] = React.useState(createInitialFilter);
    const [searchInput, setSearchInput] = React.useState("");
    const [isFilterOpen, setIsFilterOpen] = React.useState(false);
    const [loadedImages, setLoadedImages] = React.useState({});
    const [games, setGames] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [tags, setTags] = React.useState([]);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [knownLastPage, setKnownLastPage] = React.useState(1);
    const [hasMore, setHasMore] = React.useState(false);
    const [isLoadingGames, setIsLoadingGames] = React.useState(false);
    const [isLoadingFilters, setIsLoadingFilters] = React.useState(true);

    React.useEffect(() => {
        document.title = "Games - CGES.CLUB";
    }, []);

    React.useEffect(() => {
        if (!isFilterOpen) return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isFilterOpen]);

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            setFilters((prev) => ({ ...prev, search: searchInput.trim() }));
        }, 350);
        return () => clearTimeout(timeout);
    }, [searchInput]);

    const loadFilters = React.useCallback(async () => {
        setIsLoadingFilters(true);
        try {
            const response = await makeAPI("/api/get_game_info/filters", "POST", false, {});
            if (response.status === "S") {
                setCategories(response.filters?.categories || []);
                setTags(response.filters?.tags || []);
            }
        } catch (error) {
            console.error("Error loading game filters:", error);
        } finally {
            setIsLoadingFilters(false);
        }
    }, []);

    const fetchGames = React.useCallback(async (pageNum, activeFilters) => {
        setIsLoadingGames(true);
        try {
            const response = await makeAPI("/api/get_game_info/catalog", "POST", false, {
                page: pageNum,
                limit: PAGE_SIZE,
                filter_data: activeFilters
            });

            if (response.status === "S") {
                const nextGames = response.games || [];
                const nextHasMore = Boolean(response.has_more);

                setGames(nextGames);
                setCurrentPage(pageNum);
                setHasMore(nextHasMore);
                setKnownLastPage((prev) => Math.max(prev, pageNum + (nextHasMore ? 1 : 0)));
            } else {
                setGames([]);
                setHasMore(false);
                setCurrentPage(pageNum);
                setKnownLastPage(pageNum);
            }
        } catch (error) {
            console.error("Error loading games:", error);
            setGames([]);
            setHasMore(false);
        } finally {
            setIsLoadingGames(false);
        }
    }, []);

    React.useEffect(() => {
        loadFilters();
    }, [loadFilters]);

    React.useEffect(() => {
        setCurrentPage(1);
        setKnownLastPage(1);
        setHasMore(false);
        fetchGames(1, filters);
    }, [filters, fetchGames]);

    const toggleArrayFilter = (key, value) => {
        setFilters((prev) => {
            const exists = prev[key].includes(value);
            return {
                ...prev,
                [key]: exists ? prev[key].filter((item) => item !== value) : [...prev[key], value]
            };
        });
    };

    const clearFilters = () => {
        setSearchInput("");
        setFilters(createInitialFilter());
        setIsFilterOpen(false);
    };

    const visiblePages = React.useMemo(() => {
        const maxPage = Math.max(knownLastPage, currentPage, hasMore ? currentPage + 1 : currentPage);
        const start = Math.max(1, currentPage - 1);
        const end = Math.min(maxPage, currentPage + 1);
        const pages = [];

        for (let pageNum = start; pageNum <= end; pageNum += 1) {
            pages.push(pageNum);
        }

        if (!pages.includes(1)) {
            pages.unshift(1);
        }

        return [...new Set(pages)];
    }, [currentPage, hasMore, knownLastPage]);

    const canGoNext = hasMore || currentPage < knownLastPage;

    return (
        <>
            <style>{`
                .gl-page{--page:#000000;--bg:#121212;--soft:#1d1d1d;--border:rgba(255,255,255,.07);--text:#f7f7f7;--muted:#9a9a9a;--accent:#f46119;--accent2:#ff7a33;color:var(--text);padding:12px 0 28px;background:#000}
                .gl-layout{display:grid;grid-template-columns:minmax(0,1fr) 320px;gap:24px;align-items:start}
                .gl-panel{background:#111111;border:1px solid var(--border);border-radius:18px;box-shadow:0 24px 60px rgba(0,0,0,.28)}
                .gl-side{padding:22px;position:sticky;top:12px}
                .gl-side-head{display:none}
                .gl-main{display:flex;flex-direction:column;gap:20px;min-width:0}
                .gl-hero{padding:28px 28px 24px;background:linear-gradient(145deg,rgba(255,255,255,.05),rgba(255,255,255,.01));overflow:hidden;position:relative}
                .gl-hero:before{content:"";position:absolute;inset:auto -60px -100px auto;width:240px;height:240px;border-radius:999px;background:radial-gradient(circle,rgba(244,97,25,.22),rgba(244,97,25,0) 68%);pointer-events:none}
                .gl-eyebrow{display:inline-flex;align-items:center;gap:8px;margin-bottom:12px;color:#ff9b62;font-size:12px;font-weight:800;letter-spacing:.12em;text-transform:uppercase}
                .gl-eyebrow:before{content:"";width:28px;height:1px;background:currentColor;opacity:.7}
                .gl-title{margin:0 0 8px;color:#fff;font-size:30px;font-weight:800;letter-spacing:-.03em}
                .gl-copy{margin:0;color:var(--muted);font-size:14px;line-height:1.65}
                .gl-group{margin-top:20px}
                .gl-label{display:block;margin-bottom:10px;color:#fff;font-size:12px;font-weight:800;letter-spacing:.08em;text-transform:uppercase}
                .gl-input,.gl-select{width:100%;min-height:46px;padding:0 16px;border-radius:12px;border:1px solid rgba(255,255,255,.08);background:var(--soft);color:#fff}
                .gl-input::placeholder{color:#748198}
                .gl-select option{color:#fff;background:#1d1d1d}
                .gl-chipbox{display:flex;flex-wrap:wrap;gap:8px;max-height:240px;overflow:auto;padding-right:4px}
                .gl-chip{border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03);color:var(--muted);border-radius:999px;padding:8px 11px;font-size:12px;line-height:1.3;cursor:pointer;transition:.2s}
                .gl-chip:hover,.gl-chip.active{background:rgba(244,97,25,.14);border-color:rgba(244,97,25,.46);color:#fff;transform:translateY(-1px)}
                .gl-actions{display:flex;gap:12px;margin-top:22px}
                .gl-btn{flex:1;min-height:46px;border-radius:12px;border:0;font-weight:700;cursor:pointer;transition:.2s}
                .gl-btn:hover{transform:translateY(-1px)}
                .gl-btn.primary{background:linear-gradient(135deg,var(--accent),var(--accent2));color:#fff}
                .gl-btn.ghost{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);color:#fff}
                .gl-results{display:flex;justify-content:space-between;align-items:center;gap:14px;padding:0 4px}
                .gl-results p{margin:0;color:var(--muted)}
                .gl-grid{display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:14px}
                .gl-card{position:relative;overflow:hidden;border-radius:8px;border:1px solid rgba(255,255,255,.06);background:#0f0f0f;cursor:pointer;transition:.22s}
                .gl-card:hover{transform:translateY(-2px);border-color:rgba(255,255,255,.14);box-shadow:0 14px 24px rgba(0,0,0,.22)}
                .gl-card-media{position:relative;aspect-ratio:16/9;overflow:hidden;background:radial-gradient(circle at center,rgba(255,255,255,.08),transparent 58%),linear-gradient(160deg,#181818,#0b0b0b)}
                .gl-card-media.fallback{background:#121212}
                .gl-card-media-bg{position:absolute;inset:-10%;background-size:cover;background-position:center;filter:blur(18px) saturate(1.05);opacity:.58;transform:scale(1.08)}
                .gl-card-media:after{content:"";position:absolute;inset:0;background:linear-gradient(180deg,rgba(0,0,0,0) 18%,rgba(0,0,0,.18) 46%,rgba(0,0,0,.62) 74%,rgba(0,0,0,.96) 100%);opacity:.98;transition:.24s}
                .gl-card-media img{position:relative;z-index:1;display:block;width:100%;height:100%;object-fit:contain;object-position:center;transition:transform .32s ease,opacity .2s ease;padding:2px;opacity:0}
                .gl-card-media img.loaded{opacity:1}
                .gl-card:hover .gl-card-media img{transform:scale(1.02)}
                .gl-card-loader{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;z-index:1;background:
                    radial-gradient(circle at center,rgba(255,255,255,.07),transparent 26%),
                    linear-gradient(90deg,rgba(255,255,255,.02),rgba(255,255,255,.06),rgba(255,255,255,.02));
                    background-size:auto,200% 100%;
                    animation:gl-shimmer 1.2s linear infinite}
                .gl-card-loader:before{content:"";width:28px;height:28px;border-radius:999px;border:2px solid rgba(255,255,255,.18);border-top-color:rgba(244,97,25,.95);animation:gl-spin .8s linear infinite}
                .gl-card-body{position:absolute;left:0;right:0;bottom:0;z-index:2;padding:34px 12px 10px;background:linear-gradient(0deg,rgba(0,0,0,.96) 0%,rgba(0,0,0,.72) 46%,rgba(0,0,0,0) 100%);transform:translateY(18px);opacity:0;transition:.24s}
                .gl-card:hover .gl-card-body,.gl-card:focus-within .gl-card-body{transform:translateY(0);opacity:1}
                .gl-card-title{margin:0;color:#fff;font-size:13px;font-weight:800;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
                .gl-card-copy{display:none}
                .gl-open{position:absolute;top:50%;left:50%;display:inline-flex;align-items:center;justify-content:center;width:42px;height:42px;border-radius:999px;border:1px solid rgba(255,255,255,.72);background:rgba(0,0,0,.38);color:#fff;font-size:13px;transition:opacity .2s ease,transform .2s ease,background .2s ease,border-color .2s ease;opacity:0;transform:translate(-50%,-42%);pointer-events:none;box-shadow:0 10px 22px rgba(0,0,0,.22);backdrop-filter:blur(3px)}
                .gl-open i{margin-left:2px}
                .gl-card:hover .gl-open,.gl-card:focus-within .gl-open{opacity:1;transform:translate(-50%,-50%);pointer-events:auto}
                .gl-card:hover .gl-open{background:rgba(244,97,25,.9);border-color:rgba(244,97,25,.9)}
                .gl-empty{padding:36px 28px;text-align:center;color:var(--muted);border-radius:24px;border:1px dashed rgba(255,255,255,.12);background:rgba(255,255,255,.02)}
                .gl-pagination{display:flex;justify-content:center;align-items:center;flex-wrap:wrap;gap:10px;padding-top:6px}
                .gl-pagebtn{min-width:44px;height:44px;padding:0 14px;border-radius:10px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.03);color:#fff;font-size:13px;font-weight:700;cursor:pointer;transition:.2s}
                .gl-pagebtn:hover:not(:disabled){transform:translateY(-1px);border-color:rgba(244,97,25,.4);background:rgba(244,97,25,.12)}
                .gl-pagebtn.active{background:linear-gradient(135deg,var(--accent),var(--accent2));border-color:transparent;color:#fff}
                .gl-pagebtn:disabled{opacity:.45;cursor:not-allowed}
                .gl-filterfab{display:none}
                .gl-backdrop{display:none}
                .gl-close{width:38px;height:38px;border-radius:10px;border:1px solid rgba(255,255,255,.08);background:rgba(255,255,255,.04);color:#fff;cursor:pointer}
                @keyframes gl-spin{to{transform:rotate(360deg)}}
                @keyframes gl-shimmer{0%{background-position:0 0,-200% 0}100%{background-position:0 0,200% 0}}
                @media (min-width:1600px){.gl-grid{grid-template-columns:repeat(5,minmax(0,1fr))}}
                @media (max-width:1399px){.gl-layout{grid-template-columns:minmax(0,1fr) 290px}.gl-grid{grid-template-columns:repeat(4,minmax(0,1fr))}}
                @media (max-width:1199px){
                    .gl-layout{grid-template-columns:1fr}
                    .gl-side{position:fixed;top:auto;right:0;bottom:0;left:0;z-index:40;border-radius:18px 18px 0 0;max-height:min(82vh,760px);overflow:auto;transform:translateY(102%);transition:transform .24s ease;order:0;padding:18px 18px calc(18px + env(safe-area-inset-bottom))}
                    .gl-side.open{transform:translateY(0)}
                    .gl-side-head{display:flex;justify-content:space-between;align-items:center;gap:12px;margin-bottom:6px}
                    .gl-side-title{margin:0;color:#fff;font-size:24px;font-weight:800}
                    .gl-backdrop{display:block;position:fixed;inset:0;background:rgba(0,0,0,.58);backdrop-filter:blur(3px);z-index:35;opacity:0;pointer-events:none;transition:opacity .24s ease}
                    .gl-backdrop.open{opacity:1;pointer-events:auto}
                    .gl-filterfab{position:fixed;right:16px;bottom:calc(16px + env(safe-area-inset-bottom));z-index:30;display:inline-flex;align-items:center;gap:10px;min-height:48px;padding:0 16px;border:0;border-radius:999px;background:linear-gradient(135deg,var(--accent),var(--accent2));color:#fff;font-size:13px;font-weight:800;letter-spacing:.04em;box-shadow:0 16px 34px rgba(0,0,0,.32);cursor:pointer}
                    .gl-grid{grid-template-columns:repeat(3,minmax(0,1fr))}
                }
                @media (hover:none){.gl-card-media:after{opacity:1}.gl-card-body{transform:translateY(0);opacity:1;padding-top:34px}.gl-open{opacity:1;transform:translate(-50%,-50%);pointer-events:auto}}
                @media (max-width:767px){.gl-grid{grid-template-columns:repeat(2,minmax(0,1fr));gap:12px}.gl-actions,.gl-results{flex-direction:column;align-items:stretch}.gl-hero{padding:24px 20px 20px}.gl-title{font-size:24px}.gl-card-title{font-size:12px}.gl-pagination{justify-content:stretch;gap:8px}.gl-pagebtn{flex:1;min-width:0;padding:0 10px}.gl-open{width:38px;height:38px;font-size:12px}.gl-filterfab{left:16px;right:16px;justify-content:center}}
                @media (max-width:479px){.gl-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.gl-pagebtn{height:40px;font-size:12px}}
            `}</style>

            <div className="gl-page">
                <button
                    type="button"
                    className={`gl-backdrop ${isFilterOpen ? "open" : ""}`}
                    aria-label="Close filters"
                    onClick={() => setIsFilterOpen(false)}
                />

                <div className="gl-layout">
                    <section className="gl-main">
                        <div className="gl-panel gl-hero">
                            <span className="gl-eyebrow">Arcade Library</span>
                            <h1 className="gl-title">Free Games Catalog</h1>
                            <p className="gl-copy">
                                Browse the latest instant-play titles, narrow them by device or genre, and jump straight into the game page you want.
                            </p>
                        </div>

                        <div className="gl-results">
                            <p>{isLoadingGames && games.length === 0 ? "Loading catalog..." : `Showing page ${currentPage} with ${games.length} games.`}</p>
                            {/* <p>Backend API namespace: <strong>/api/get_game_info</strong></p> */}
                        </div>

                        {games.length > 0 ? (
                            <div className="gl-grid">
                                {games.map((game) => {
                                    const imageData = buildGameImage(game);
                                    const imageKey = imageData.url || game.game_id;
                                    const isLoaded = Boolean(loadedImages[imageKey]);
                                    return (
                                        <article
                                            key={game.game_id}
                                            className="gl-card"
                                            onClick={() => navigate(`/games/${slugifyGame(game)}`)}
                                        >
                                            <div className={`gl-card-media ${imageData.hasPreferred ? "" : "fallback"}`}>
                                                {!imageData.hasPreferred && imageData.url ? (
                                                    <div
                                                        className="gl-card-media-bg"
                                                        style={{ backgroundImage: `url(${imageData.url})` }}
                                                    />
                                                ) : null}
                                                {!isLoaded ? <div className="gl-card-loader" /> : null}
                                                {imageData.url ? (
                                                    <img
                                                        src={imageData.url}
                                                        alt={game.title}
                                                        className={isLoaded ? "loaded" : ""}
                                                        loading="lazy"
                                                        onLoad={() => {
                                                            setLoadedImages((prev) => ({ ...prev, [imageKey]: true }));
                                                        }}
                                                        onError={(e) => {
                                                            e.currentTarget.style.display = "none";
                                                            setLoadedImages((prev) => ({ ...prev, [imageKey]: true }));
                                                        }}
                                                    />
                                                ) : null}
                                                <span className="gl-open">
                                                    <i className="fa-solid fa-play"></i>
                                                </span>
                                            </div>
                                            <div className="gl-card-body">
                                                <h3 className="gl-card-title">{game.title}</h3>
                                            </div>
                                        </article>
                                    );
                                })}
                            </div>
                        ) : !isLoadingGames ? (
                            <div className="gl-empty">No games matched the current filters. Try clearing a few selections.</div>
                        ) : null}

                        {games.length > 0 ? (
                            <div className="gl-pagination">
                                <button
                                    type="button"
                                    className="gl-pagebtn"
                                    disabled={isLoadingGames || currentPage === 1}
                                    onClick={() => fetchGames(currentPage - 1, filters)}
                                >
                                    Prev
                                </button>

                                {visiblePages.map((pageNum) => (
                                    <button
                                        key={pageNum}
                                        type="button"
                                        className={`gl-pagebtn ${pageNum === currentPage ? "active" : ""}`}
                                        disabled={isLoadingGames}
                                        onClick={() => fetchGames(pageNum, filters)}
                                    >
                                        {pageNum}
                                    </button>
                                ))}

                                <button
                                    type="button"
                                    className="gl-pagebtn"
                                    disabled={isLoadingGames || !canGoNext}
                                    onClick={() => fetchGames(currentPage + 1, filters)}
                                >
                                    Next
                                </button>
                            </div>
                        ) : null}
                    </section>

                    <aside className={`gl-panel gl-side ${isFilterOpen ? "open" : ""}`}>
                        <div className="gl-side-head">
                            <h2 className="gl-side-title">Filters</h2>
                            <button type="button" className="gl-close" aria-label="Close filters" onClick={() => setIsFilterOpen(false)}>
                                <i className="fa-solid fa-xmark"></i>
                            </button>
                        </div>

                        <h2 className="gl-title" style={{ fontSize: "28px" }}>Filter</h2>
                        <p className="gl-copy">Catalog page only. Choose a game here, then open its dedicated player page.</p>

                        <div className="gl-group">
                            <label className="gl-label">Search</label>
                            <input
                                className="gl-input"
                                type="search"
                                placeholder="Title, studio, tag..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                        </div>

                        <div className="gl-group">
                            <label className="gl-label">Sort By</label>
                            <select
                                className="gl-select"
                                value={filters.sortby}
                                onChange={(e) => setFilters((prev) => ({ ...prev, sortby: e.target.value }))}
                            >
                                {SORT_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="gl-group">
                            <label className="gl-label">Device</label>
                            <select
                                className="gl-select"
                                value={filters.mobile_mode}
                                onChange={(e) => setFilters((prev) => ({ ...prev, mobile_mode: e.target.value }))}
                            >
                                <option value="">All Devices</option>
                                <option value="mobile">Mobile Compatible</option>
                                <option value="desktop">Desktop Focused</option>
                            </select>
                        </div>

                        <div className="gl-group">
                            <label className="gl-label">Categories</label>
                            <div className="gl-chipbox">
                                {categories.map((category) => (
                                    <button
                                        key={category.slug}
                                        type="button"
                                        className={`gl-chip ${filters.category_slugs.includes(category.slug) ? "active" : ""}`}
                                        onClick={() => toggleArrayFilter("category_slugs", category.slug)}
                                    >
                                        {category.name} ({category.total_games})
                                    </button>
                                ))}
                                {!isLoadingFilters && categories.length === 0 ? <span className="gl-copy">No categories found.</span> : null}
                            </div>
                        </div>

                        <div className="gl-group">
                            <label className="gl-label">Tags</label>
                            <div className="gl-chipbox">
                                {tags.map((tag) => (
                                    <button
                                        key={tag.slug}
                                        type="button"
                                        className={`gl-chip ${filters.tag_slugs.includes(tag.slug) ? "active" : ""}`}
                                        onClick={() => toggleArrayFilter("tag_slugs", tag.slug)}
                                    >
                                        #{tag.name}
                                    </button>
                                ))}
                                {!isLoadingFilters && tags.length === 0 ? <span className="gl-copy">No tags found.</span> : null}
                            </div>
                        </div>

                        <div className="gl-actions">
                            <button type="button" className="gl-btn ghost" onClick={clearFilters}>Clear</button>
                            <button
                                type="button"
                                className="gl-btn primary"
                                onClick={() => {
                                    fetchGames(currentPage, filters);
                                    setIsFilterOpen(false);
                                }}
                            >
                                Refresh
                            </button>
                        </div>
                    </aside>
                </div>

                <button type="button" className="gl-filterfab" onClick={() => setIsFilterOpen(true)}>
                    <i className="fa-solid fa-sliders"></i>
                    Filters
                </button>
            </div>
        </>
    );
}
